import React, { useEffect } from 'react';

import { ReactComponent as DeleteIcon } from 'assets/icons/deleteIcon.svg';
import { ReactComponent as EditPenIcon } from 'assets/icons/editPen.svg';
import { ReactComponent as SaveIcon } from 'assets/pics/tick.svg';
import PropTypes from 'prop-types';

import { InputField } from 'components/FormHelper/InputField';
import { TooltipIconButton } from 'components/TooltipIconButton/TooltipIconButton';

import {
  getVisibleParticipantEmailError,
  hasOriginalParticipantEmail,
} from './eventParticipantEmail.utils';

export const EventRegisterParticipantItem = ({
  index,
  participant,
  removeParticipant,
  formik,
  iamType,
  activeParticipantEmailIndex,
  setActiveParticipantEmailIndex,
  isEditingEmail,
  setParticipantEmailEditing,
  participantId,
}) => {
  const participantName =
    `${participant?.firstName || participant?.vorname || ''} ${
      participant?.lastName || participant?.nachname || ''
    }`.trim() || '-';

  const hasOriginalEmail = hasOriginalParticipantEmail(participant);
  const participantEmail = formik.values.participants[index].eMail;
  const participantEmailError = formik.errors?.participants?.[index]?.eMail;
  const visibleEmailError = getVisibleParticipantEmailError(
    participantEmailError,
    index,
    activeParticipantEmailIndex,
    participantEmail,
    formik.values.participants
  );
  const requiresEmailInput = Boolean(visibleEmailError);
  const showEmailInput = !hasOriginalEmail && (isEditingEmail || requiresEmailInput);

  useEffect(() => {
    if (!hasOriginalEmail && requiresEmailInput && !isEditingEmail) {
      setParticipantEmailEditing(participantId, true);
    }
  }, [
    hasOriginalEmail,
    requiresEmailInput,
    isEditingEmail,
    participantId,
    setParticipantEmailEditing,
  ]);

  const handleEmailFocus = () => {
    setActiveParticipantEmailIndex(index);
  };

  const handleEmailChange = (event) => {
    setActiveParticipantEmailIndex(index);
    formik.handleChange(event);
  };

  const handleEditEmail = () => {
    setActiveParticipantEmailIndex(index);
    setParticipantEmailEditing(participantId, true);
  };

  const closeEmailEditingIfValid = () => {
    if (visibleEmailError) {
      return;
    }

    setParticipantEmailEditing(participantId, false);
    if (activeParticipantEmailIndex === index) {
      setActiveParticipantEmailIndex(null);
    }
  };

  const handleEmailBlur = (event) => {
    formik.handleBlur(event);
    closeEmailEditingIfValid();
  };

  let participantTypeLabel = 'Gast';
  if (participant.fachkraftId) {
    participantTypeLabel = 'Fachkraft';
  } else if (iamType === 'EIAM') {
    participantTypeLabel = 'Mitarbeiter';
  }

  return (
    <div>
      <div className="event-register-form-new-participant-fields">
        <div className="event-register-form__participant-name">
          <div className="event-register-form__position">{index + 1}</div>
          <div className="event-register-form__participant-name-wrapper">
            <div>{participantName}</div>
          </div>
        </div>
        <div className="event-register-form__participant-updates">
          <div className="event-register-form__participant-email-wrapper event-register-form__participant-email-wrapper--responsive">
            {!showEmailInput ? (
              <div className="event-register-form__participant-email-readonly event-register-form__participant-email-readonly--breakable">
                <div className="event-register-form__participant-email-readonly-text">
                  {participantEmail}
                </div>
                {!hasOriginalEmail && (
                  <div>
                    <TooltipIconButton
                      className="event-register-form__icon-button"
                      aria-label="E-Mail-Adresse bearbeiten"
                      onClick={handleEditEmail}>
                      <EditPenIcon />
                    </TooltipIconButton>
                  </div>
                )}
              </div>
            ) : (
              <div className="event-register-form__participant-email-input-wrapper">
                <InputField
                  value={participantEmail}
                  fullWidth
                  label="E-Mail-Adresse*"
                  name={`participants.${index}.eMail`}
                  disabled={false}
                  onChange={handleEmailChange}
                  onFocus={handleEmailFocus}
                  onBlur={handleEmailBlur}
                  key={`${participant.id}-email`}
                  id={`${participant.id}-email`}
                  inputProps={{
                    autoComplete: `event-participant-${participant.id}-email`,
                  }}
                  error={Boolean(visibleEmailError)}
                  helperText={visibleEmailError || ''}
                />
                <TooltipIconButton
                  className="event-register-form__icon-button-save"
                  aria-label="E-Mail-Adresse speichern"
                  disabled={Boolean(visibleEmailError)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={closeEmailEditingIfValid}>
                  <SaveIcon />
                </TooltipIconButton>
              </div>
            )}
          </div>
          <div className="event-register-form__participant-type">{participantTypeLabel}</div>
          <TooltipIconButton
            className="event-register-form__icon-button"
            aria-label="Fachkraft löschen"
            onClick={(event) => removeParticipant(event, index)}>
            <DeleteIcon />
          </TooltipIconButton>
        </div>
      </div>
      <div className="event-register-form__separator" />
    </div>
  );
};

EventRegisterParticipantItem.propTypes = {
  index: PropTypes.number.isRequired,
  participant: PropTypes.object.isRequired,
  removeParticipant: PropTypes.func.isRequired,
  formik: PropTypes.object.isRequired,
  iamType: PropTypes.string,
  activeParticipantEmailIndex: PropTypes.number,
  setActiveParticipantEmailIndex: PropTypes.func.isRequired,
  isEditingEmail: PropTypes.bool.isRequired,
  setParticipantEmailEditing: PropTypes.func.isRequired,
  participantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
